import React, { useState } from 'react';
import { Loader2, Wand2, ImagePlus, Check, Music, Trash2, Upload } from 'lucide-react';
import api, { mediaUrl, uploadImage, uploadContentMedia, errText } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import RichTextEditor from './RichTextEditor';
import VoiceRecorder from './VoiceRecorder';

const CATEGORIES = ['Blog', 'Poem', 'Story', 'Yoga & Wellness', 'Healing & Therapy', 'Meditation', 'Podcast'];

const BLANK = {
  title: '', category: 'Blog', author: '', excerpt: '', content: '', cover_image: '',
  audio_url: '', video_url: '', seo_title: '', seo_description: '', tags: [], is_published: false,
};

// Reusable blog authoring composer with AI drafting, voice input and media.
// Props: createEndpoint, updateEndpoint(id) -> string, initial (post|null), canPublish, onSaved, onCancel
const BlogComposer = ({ createEndpoint, updateEndpoint, initial = null, canPublish = false, onSaved, onCancel }) => {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [covering, setCovering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(initial ? { ...BLANK, ...initial, tags: initial.tags || [] } : BLANK);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const draft = async () => {
    if (prompt.trim().length < 5) return toast({ title: 'Describe your post idea first' });
    setDrafting(true);
    try {
      const r = await api.post('/assistant/draft-blog', { text: prompt });
      const d = r.data.draft || {};
      setF((p) => ({
        ...p,
        title: d.title || p.title,
        excerpt: d.excerpt || p.excerpt,
        content: d.content || p.content,
        category: CATEGORIES.includes(d.category) ? d.category : p.category,
        tags: Array.isArray(d.tags) ? d.tags : p.tags,
        seo_title: d.seo_title || p.seo_title,
        seo_description: d.seo_description || p.seo_description,
      }));
      toast({ title: 'Draft ready — review & edit below 🌸' });
    } catch (e) { toast({ title: 'Drafting failed', description: errText(e) }); }
    finally { setDrafting(false); }
  };

  const makeCover = async () => {
    if (!f.title) return toast({ title: 'Add a title first' });
    setCovering(true);
    try {
      const r = await api.post('/assistant/generate-cover', { title: f.title, subtitle: f.excerpt, style: 'serene editorial blog cover art, soft maroon and cream, calm sacred atmosphere, no text' });
      set('cover_image', r.data.url);
      toast({ title: 'Cover generated 🎨' });
    } catch (e) { toast({ title: 'Cover failed', description: errText(e) }); }
    finally { setCovering(false); }
  };

  const uploadCover = async (file) => {
    try { const url = await uploadImage(file); set('cover_image', url); toast({ title: 'Image uploaded' }); }
    catch (e) { toast({ title: 'Upload failed', description: errText(e) }); }
  };

  const uploadAudio = async (file) => {
    try { const { url } = await uploadContentMedia(file); set('audio_url', url); toast({ title: 'Audio uploaded' }); }
    catch (e) { toast({ title: 'Audio upload failed', description: errText(e) }); }
  };

  const save = async (publish) => {
    if (!f.title.trim()) return toast({ title: 'Title is required' });
    setSaving(true);
    const payload = {
      title: f.title, category: f.category, author: f.author || undefined, excerpt: f.excerpt,
      content: f.content, cover_image: f.cover_image || null, audio_url: f.audio_url || null,
      video_url: f.video_url || null, seo_title: f.seo_title || null, seo_description: f.seo_description || null,
      tags: Array.isArray(f.tags) ? f.tags : [],
    };
    if (canPublish) payload.is_published = publish;
    try {
      if (initial?.id) await api.patch(updateEndpoint(initial.id), payload);
      else await api.post(createEndpoint, payload);
      toast({ title: initial?.id ? 'Post saved' : (publish ? 'Post published' : 'Draft saved') });
      onSaved?.();
    } catch (e) { toast({ title: 'Save failed', description: errText(e) }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="blog-composer">
      {/* AI + Voice */}
      <div className="rounded-2xl border border-rose/40 bg-white/70 p-5 space-y-3">
        <span className="block text-xs uppercase tracking-widest text-mahogany/70">Describe your post — or dictate it</span>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} data-testid="composer-prompt"
          placeholder="e.g. A short reflective piece on how morning meditation dissolves anxiety, with 3 practical tips…"
          className="inp" />
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={draft} disabled={drafting} className="pill-btn" data-testid="composer-draft-btn">
            {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Draft with AI
          </button>
          <VoiceRecorder label="Dictate" testid="composer-voice" onTranscript={(t) => setPrompt((p) => (p ? p + ' ' : '') + t)} />
        </div>
      </div>

      {/* Fields */}
      <div className="rounded-2xl border border-rose/40 bg-white/70 p-5 space-y-4">
        <Txt label="Title *" value={f.title} onChange={(e) => set('title', e.target.value)} testid="composer-title" />
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className="lbl">Category</span>
            <select value={f.category} onChange={(e) => set('category', e.target.value)} className="inp" data-testid="composer-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Txt label="Author" value={f.author} onChange={(e) => set('author', e.target.value)} />
        </div>
        <Txt label="Excerpt" value={f.excerpt} onChange={(e) => set('excerpt', e.target.value)} testid="composer-excerpt" />

        <div>
          <span className="lbl">Content</span>
          <RichTextEditor value={f.content} onChange={(html) => set('content', html)} />
        </div>

        {/* Cover */}
        <div>
          <span className="lbl">Cover image</span>
          <div className="flex flex-wrap items-center gap-3">
            {f.cover_image
              ? <img src={mediaUrl(f.cover_image)} alt="cover" className="w-28 h-20 rounded-xl object-cover" data-testid="composer-cover" />
              : <div className="w-28 h-20 rounded-xl bg-rose-soft/40 flex items-center justify-center text-mahogany/40"><ImagePlus className="w-5 h-5" /></div>}
            <label className="pill-btn-outline cursor-pointer text-sm"><Upload className="w-4 h-4" /> Upload
              <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => e.target.files[0] && uploadCover(e.target.files[0])} /></label>
            <button onClick={makeCover} disabled={covering} className="pill-btn-outline text-sm" data-testid="composer-cover-ai-btn">
              {covering ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Generate with AI
            </button>
            {f.cover_image && <button onClick={() => set('cover_image', '')} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
          </div>
        </div>

        {/* Audio blog */}
        <div>
          <span className="lbl">Audio blog (optional narration / recording)</span>
          <div className="flex flex-wrap items-center gap-3">
            <VoiceRecorder label="Record audio" testid="composer-audio"
              onTranscript={() => {}}
              onAudio={({ url }) => { if (url) { set('audio_url', url); toast({ title: 'Recording attached 🎙️' }); } }} />
            <label className="pill-btn-outline cursor-pointer text-sm"><Music className="w-4 h-4" /> Upload audio
              <input type="file" accept="audio/*" className="hidden" onChange={(e) => e.target.files[0] && uploadAudio(e.target.files[0])} /></label>
            {f.audio_url && <audio controls src={mediaUrl(f.audio_url)} className="h-9" data-testid="composer-audio-player" />}
            {f.audio_url && <button onClick={() => set('audio_url', '')} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
          </div>
        </div>

        {/* Vlog */}
        <Txt label="Vlog video URL (YouTube / Vimeo, optional)" value={f.video_url} onChange={(e) => set('video_url', e.target.value)} testid="composer-video" placeholder="https://youtube.com/watch?v=…" />

        {/* SEO */}
        <div className="rounded-xl border border-rose/30 bg-rose-soft/20 p-4 space-y-3">
          <span className="block text-xs uppercase tracking-widest text-mahogany/70">Search / SEO</span>
          <Txt label="SEO title (≤ 60 chars)" value={f.seo_title} onChange={(e) => set('seo_title', e.target.value)} testid="composer-seo-title" />
          <Txt label="Meta description (≤ 155 chars)" value={f.seo_description} onChange={(e) => set('seo_description', e.target.value)} testid="composer-seo-desc" />
        </div>

        <Txt label="Tags (comma separated)" value={(f.tags || []).join(', ')} onChange={(e) => set('tags', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button onClick={() => save(false)} disabled={saving} className="pill-btn-outline" data-testid="composer-save-draft-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save draft
        </button>
        {canPublish && (
          <button onClick={() => save(true)} disabled={saving} className="pill-btn" data-testid="composer-publish-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {initial?.is_published ? 'Save & keep published' : 'Publish now'}
          </button>
        )}
        {onCancel && <button onClick={onCancel} className="pill-btn-outline">Cancel</button>}
      </div>

      <style>{`.inp{width:100%;background:#fff;border:1px solid rgba(191,131,143,.5);border-radius:.6rem;padding:.5rem .75rem;font-size:.9rem;color:#4A1721}.lbl{display:block;font-size:.7rem;text-transform:uppercase;letter-spacing:.12em;color:rgba(74,23,33,.7);margin-bottom:.35rem}`}</style>
    </div>
  );
};

const Txt = ({ label, testid, ...p }) => (
  <label className="block"><span className="lbl">{label}</span>
    <input {...p} data-testid={testid} className="inp" /></label>
);

export default BlogComposer;
